import { Button } from '@asgard/ui-library';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FormCheckbox } from 'components/form/FormCheckbox';
import { FormPhone } from 'components/form/FormPhone';
import { FormText } from 'components/form/FormText/FormText';
import { FormTextarea } from 'components/form/FormTextarea';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LinkText } from 'components/shared/LinkText';
import { Heading2 } from 'components/shared/Typography.styled';
import { PHONE_NUMBER_DEFAULT_COUNTRY } from 'features/portal/v2/contact/basic-info/ContactBasicInfo.constants';
import { Trans } from 'next-i18next';
import { FormProvider } from 'react-hook-form';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { useCompaniesForm } from './Form.hooks';
import {
  StyledFormHeader,
  StyledHero,
  StyledHeroContainer,
  StyledHeroForm,
  StyledHeroText,
  StyledHeroTitle,
  StyledHeroWrapper,
  StyledTerms
} from './Hero.styled';

const Hero: React.FC = () => {
  const {
    t,
    formMethods,
    locale,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  } = useCompaniesForm();

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = formMethods;

  return (
    <StyledHeroWrapper>
      <StyledHero className="companiesHero">
        <StyledHeroContainer>
          <StyledHeroText>
            <StyledHeroTitle>{t('companies.hero.title')}</StyledHeroTitle>
            <HtmlTrans ns="companies2" i18nKey="companies.hero.desc" />
          </StyledHeroText>
          <StyledHeroForm onSubmit={handleSubmit(onSubmit)}>
            <StyledFormHeader>
              <Heading2 editorial>{t('companies.form.title')}</Heading2>
              <p>
                <HtmlTrans ns="companies2" i18nKey="companies.form.desc" />
              </p>
            </StyledFormHeader>
            <FormProvider {...formMethods}>
              <FormText
                name="name"
                formGroup={{
                  label: t('companies.form.name')
                }}
                input={{
                  placeholder: t('companies.form.name_placeholder'),
                  onFocus: onFormFieldFocus
                }}
              />
              <FormText
                name="email"
                formGroup={{
                  label: t('companies.form.email')
                }}
                input={{
                  placeholder: t('companies.form.email_placeholder'),
                  onFocus: onFormFieldFocus
                }}
              />
              <div className="phoneNumber">
                <FormPhone
                  name="contactPhone"
                  formGroup={{
                    label: t('companies.form.phone')
                  }}
                  input={{
                    defaultCountry: PHONE_NUMBER_DEFAULT_COUNTRY,
                    onFocus: onFormFieldFocus
                  }}
                />
              </div>
              <FormText
                name="company"
                formGroup={{
                  label: t('companies.form.company')
                }}
                input={{
                  placeholder: t('companies.form.company_placeholder'),
                  onFocus: onFormFieldFocus
                }}
              />
              <FormTextarea
                name="description"
                formGroup={{
                  label: t('companies.form.description')
                }}
                input={{
                  placeholder: t('companies.form.description_placeholder'),
                  onFocus: onFormFieldFocus,
                  rows: 3
                }}
              />
              <FormCheckbox
                name="terms"
                input={{
                  onFocus: onFormFieldFocus,
                  label: (
                    <StyledTerms>
                      <Trans
                        ns={'companies2'}
                        i18nKey={'companies.form.terms'}
                        components={{
                          privatePolicy: (
                            <LinkText
                              href={getAppUrl(APP_URLS.privacyPolicy, locale)}
                            />
                          ),
                          terms: (
                            <LinkText
                              href={getAppUrl(APP_URLS.termsOfUse, locale)}
                            />
                          )
                        }}
                      ></Trans>
                    </StyledTerms>
                  )
                }}
              />

              <FormCheckbox
                name="isSubscribeToNewsletter"
                input={{
                  onFocus: onFormFieldFocus,
                  label: (
                    <StyledTerms>
                      <Trans
                        ns={'companies2'}
                        i18nKey={'companies.form.newsletter'}
                        components={{
                          newsletter: (
                            <a
                              href="https://none.clickacm.com/show/qgJ2lZXPBBizIfJi3pmS/"
                              target="_blank"
                            />
                          )
                        }}
                      ></Trans>
                    </StyledTerms>
                  )
                }}
              />
              <Button
                schema="brand2"
                label={t('companies.form.submit')}
                startIcon={faCheckCircle}
                type="submit"
                disabled={!isTermsAccepted}
                isLoading={isSubmitting}
                loadingText={t('companies.form.submit_in-progress')}
              />
            </FormProvider>
          </StyledHeroForm>
        </StyledHeroContainer>
      </StyledHero>
    </StyledHeroWrapper>
  );
};

export default Hero;
